/** @jsx jsx */
import { css, jsx, keyframes } from '@emotion/react';

import { Typography2, breakpoints } from '@coursera/cds-core';

import Section from 'bundles/front-page/components/Section';
import { useAiAssistanceExperimentData } from 'bundles/front-page/providers/AiAssistanceExperimentProvider';
import CoachButton from 'bundles/front-page/redesign-modules/ai-assistance/CoachButton';

const expandHeight = keyframes`
  from {
    grid-template-rows: 0fr;
  }
  to {
    grid-template-rows: 1fr;
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
    opacity: 1;
  }
  50% {
    background-position: 100% 50%;
    opacity: 0.35;
  }
  100% {
    background-position: 0% 50%;
    opacity: 1;
  }
`;

const styles = {
  fullWidthWrapper: css`
    position: relative;
    width: 100%;
    margin-top: calc(var(--cds-spacing-200) * -1);
    padding-top: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-200);
    display: grid;
    animation: ${expandHeight} 0.6s cubic-bezier(0.32, 0.72, 0, 1) forwards;
    will-change: grid-template-rows;

    ${breakpoints.down('xs')} {
      margin-bottom: var(--cds-spacing-100);
    }

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      grid-template-rows: 1fr;
      will-change: auto;
    }
  `,
  overflowWrapper: css`
    overflow: hidden;
    min-height: 0;
    will-change: opacity;
    animation: var(--labs-animation-fade-in);
    animation-delay: 0.2s;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      opacity: 1;
      will-change: auto;
    }
  `,
  animatedBackground: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      var(--cds-color-lighten-0) 0%,
      var(--cds-color-emphasis-primary-background-default) 30%,
      /* Design-specified cyan */ #0ff0ff 70%,
      var(--cds-color-lighten-0) 100%
    );
    background-size: 200% 100%;
    filter: blur(2px);
    mask-image: linear-gradient(to bottom, var(--cds-color-darken-975) 0%, transparent 100%),
      radial-gradient(ellipse 80% 100% at 50% 0%, var(--cds-color-darken-975) 0%, transparent 100%);
    mask-composite: intersect;
    -webkit-mask-image: linear-gradient(to bottom, var(--cds-color-darken-975) 0%, transparent 100%),
      radial-gradient(ellipse 80% 100% at 50% 0%, var(--cds-color-darken-975) 0%, transparent 100%);
    -webkit-mask-composite: source-in;
    animation: ${gradientShift} 10s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      background-position: 0% 50%;
    }
  `,
  sectionContent: css`
    position: relative;
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-200);
    align-items: center;
    justify-content: center;
    padding: var(--cds-spacing-200) 0 var(--cds-spacing-400) 0;
  `,

  sideLabel: css`
    max-width: 400px;

    ${breakpoints.down('xs')} {
      text-align: center;
      text-wrap: balance;
    }
  `,
};

const AiModuleRenderer = () => {
  const { isAiAssistanceEnabled, sideLabel } = useAiAssistanceExperimentData();

  if (!isAiAssistanceEnabled) {
    return null;
  }

  return (
    <div css={styles.fullWidthWrapper} role="region" aria-live="polite">
      <div css={styles.overflowWrapper}>
        <div css={styles.animatedBackground} />
        <Section>
          <div css={styles.sectionContent}>
            <Typography2 variant="subtitleSmall" component="h2" css={styles.sideLabel}>
              {sideLabel}
            </Typography2>
            <CoachButton />
          </div>
        </Section>
      </div>
    </div>
  );
};

export default AiModuleRenderer;
